import React from 'react';

import InputLabel from 'components/inputfields/InputLabel';
import IProduct, { LampEnergyClassEnum } from 'models/product';
import { InputTextFieldDebounced } from 'components/inputfields/InputTextField';
import { FloatInputDebounced } from '../FloatInput';
import BooleanField from 'components/inputfields/BooleanField';
import ListDropDown from 'components/inputfields/ListDropDown';
import ThreeWaySwitch from 'components/inputfields/ThreeWaySwitch';

interface IElectricLampProps {
  readOnly?: boolean;
  approve: boolean;
  state: IProduct;
  setState: React.Dispatch<React.SetStateAction<IProduct>>;
}

export const energyClassOptions = Object.values(LampEnergyClassEnum).map(value => ({ value, displayName: value }));

export const ElectricLamp = ({ readOnly, approve, state, setState }: IElectricLampProps) => {
  const handleDropdownChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const stateName = event.target.name;
    const stateValue = event.target.value;
    const val = !stateValue || stateValue === '' ? null : stateValue;
    stateName && setState({ ...state, [stateName]: val } as IProduct);
  };

  const fieldStyle = { marginBottom: 0, width: 200, flex: 'none' };

  return (
    <>
      <h3>Specifikation</h3>

      <InputLabel heading="Effekt" text="Effekt angiven i watt." />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <FloatInputDebounced<IProduct>
            readOnly={readOnly}
            dense={false}
            placeholder="t.ex. 1,4"
            name="lampEffect"
            state={state}
            setState={setState}
            style={fieldStyle}
          />
          <h3 style={{ margin: 0 }}>&nbsp;W</h3>
        </div>
        {approve && state?.approve?.find(x => x.field === 'lampEffect') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampEffect" state={state} setState={setState} />
        )}
      </div>

      <InputLabel heading="Lampmodell" text="Modellbeteckning för lampan, t.ex. ST26, A60." />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <InputTextFieldDebounced<IProduct>
          readOnly={readOnly}
          placeholder="t.ex. ST26"
          inputName="lampModel"
          state={state}
          setState={setState}
          style={fieldStyle}
        />
        {approve && state?.approve?.find(x => x.field === 'lampModel') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampModel" state={state} setState={setState} />
        )}
      </div>

      <InputLabel heading="Energiklass" text="Energimärkningsklass enligt EU-förordning 2019/2015." />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <ListDropDown
          readOnly={readOnly}
          disableLabel
          displayEmpty
          listData={energyClassOptions}
          value={state.lampEnergyClass}
          targetName="lampEnergyClass"
          handleChange={handleDropdownChange}
          style={{ overflow: 'hidden', width: 200 }}
        />
        {approve && state?.approve?.find(x => x.field === 'lampEnergyClass') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampEnergyClass" state={state} setState={setState} />
        )}
      </div>

      <InputLabel heading="Teknik" text="Lampans teknik, t.ex. LED, halogen, glödtråd." />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <InputTextFieldDebounced<IProduct>
          readOnly={readOnly}
          placeholder="t.ex. LED"
          inputName="lampTechnology"
          state={state}
          setState={setState}
          style={fieldStyle}
        />
        {approve && state?.approve?.find(x => x.field === 'lampTechnology') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampTechnology" state={state} setState={setState} />
        )}
      </div>

      <InputLabel heading="Sockeltyp" text="Lampans sockeltyp, t.ex. E14, E27, GU10." />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <InputTextFieldDebounced<IProduct>
          readOnly={readOnly}
          placeholder="t.ex. E14"
          inputName="lampSocketType"
          state={state}
          setState={setState}
          style={fieldStyle}
        />
        {approve && state?.approve?.find(x => x.field === 'lampSocketType') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampSocketType" state={state} setState={setState} />
        )}
      </div>

      <InputLabel heading="Dimbar" text="Kan lampan dimras?" />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <BooleanField
          readOnly={readOnly}
          name="lampDimmable"
          state={state}
          setState={setState}
          styles={{ width: 'auto' }}
        />
        {approve && state?.approve?.find(x => x.field === 'lampDimmable') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampDimmable" state={state} setState={setState} />
        )}
      </div>

      <InputLabel
        heading="Motsvarande glödlampa"
        text="Vilken klassisk glödlampa lampans ljuseffekt motsvarar, t.ex. '25W glödlampa'."
      />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <InputTextFieldDebounced<IProduct>
          readOnly={readOnly}
          placeholder="t.ex. 25W"
          inputName="lampEquivalentBulb"
          state={state}
          setState={setState}
          style={fieldStyle}
        />
        {approve && state?.approve?.find(x => x.field === 'lampEquivalentBulb') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampEquivalentBulb" state={state} setState={setState} />
        )}
      </div>

      <InputLabel heading="Ljusflöde" text="Lampans ljusflöde angivet i lumen (lm)." />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <FloatInputDebounced<IProduct>
            readOnly={readOnly}
            dense={false}
            placeholder="t.ex. 60"
            name="lampBrightnessLm"
            state={state}
            setState={setState}
            style={fieldStyle}
          />
          <h3 style={{ margin: 0 }}>&nbsp;lm</h3>
        </div>
        {approve && state?.approve?.find(x => x.field === 'lampBrightnessLm') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampBrightnessLm" state={state} setState={setState} />
        )}
      </div>

      <InputLabel heading="Ljusfärg" text="Ljusets färgtemperatur angiven i kelvin (K)." />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <FloatInputDebounced<IProduct>
            readOnly={readOnly}
            dense={false}
            placeholder="t.ex. 2100"
            name="lampColorTemperatureK"
            state={state}
            setState={setState}
            style={fieldStyle}
          />
          <h3 style={{ margin: 0 }}>&nbsp;K</h3>
        </div>
        {approve && state?.approve?.find(x => x.field === 'lampColorTemperatureK') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampColorTemperatureK" state={state} setState={setState} />
        )}
      </div>

      <InputLabel heading="Livslängd" text="Genomsnittlig livslängd angiven i timmar (h)." />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <FloatInputDebounced<IProduct>
            readOnly={readOnly}
            dense={false}
            placeholder="t.ex. 15000"
            name="lampAverageLifespanH"
            state={state}
            setState={setState}
            style={fieldStyle}
          />
          <h3 style={{ margin: 0 }}>&nbsp;h</h3>
        </div>
        {approve && state?.approve?.find(x => x.field === 'lampAverageLifespanH') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampAverageLifespanH" state={state} setState={setState} />
        )}
      </div>

      <InputLabel heading="Färgåtergivning" text="Färgåtergivningsindex (Ra/CRI)." />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <FloatInputDebounced<IProduct>
            readOnly={readOnly}
            dense={false}
            placeholder="t.ex. 80"
            name="lampColorRenderingRa"
            state={state}
            setState={setState}
            style={fieldStyle}
          />
          <h3 style={{ margin: 0 }}>&nbsp;Ra</h3>
        </div>
        {approve && state?.approve?.find(x => x.field === 'lampColorRenderingRa') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampColorRenderingRa" state={state} setState={setState} />
        )}
      </div>

      <InputLabel heading="Energikonsumtion" text="Energiförbrukning för lampan." />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <FloatInputDebounced<IProduct>
            readOnly={readOnly}
            dense={false}
            placeholder="t.ex. 2"
            name="lampEnergyConsumption"
            state={state}
            setState={setState}
            style={fieldStyle}
          />
          <h3 style={{ margin: 0 }}>&nbsp;kWh/1000h</h3>
        </div>
        {approve && state?.approve?.find(x => x.field === 'lampEnergyConsumption') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampEnergyConsumption" state={state} setState={setState} />
        )}
      </div>

      <InputLabel heading="Strålningsvinkel" text="Lampans strålningsvinkel i grader." />
      <div style={{ flex: 1, display: 'flex', gap: 4, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <FloatInputDebounced<IProduct>
            readOnly={readOnly}
            dense={false}
            placeholder="t.ex. 360"
            name="lampBeamAngleDeg"
            state={state}
            setState={setState}
            style={fieldStyle}
          />
          <h3 style={{ margin: 0 }}>&nbsp;°</h3>
        </div>
        {approve && state?.approve?.find(x => x.field === 'lampBeamAngleDeg') && (
          <ThreeWaySwitch readOnly={readOnly} name="lampBeamAngleDeg" state={state} setState={setState} />
        )}
      </div>
    </>
  );
};

export default ElectricLamp;
